import { Profile } from './profile.model';

/* Del acceso a empresas solo hacen falta el id y el nombre; el API manda el
   objeto Company completo en GET /users/{uid}. */
export interface IAppUserCompany {
  idCompany: string;
  name: string;
}

export interface IAppUser {
  profile: string;
  lastName: string;
  names: string;
  uid: string;
  email: string;
  lastLogInAt?: string | null;
  createdAt: string;
  updatedAt: string;
  /* Solo los endpoints de /users lo mandan; /account no. */
  companiesCount?: number;
  /* Solo GET /users/{uid}. */
  companies?: IAppUserCompany[];
}

/* Body de POST /users. Sin password (o vacío) la API genera una de 12
   caracteres y la regresa en data.generatedPassword. */
export interface IAppUserCreate {
  names: string;
  lastName: string;
  email: string;
  profile: string;
  password?: string;
}

/* Body de PUT /users/{uid}: no lleva password. */
export interface IAppUserUpdate {
  names: string;
  lastName: string;
  email: string;
  profile: string;
}

export class AppUser {
  profile: string;
  lastName: string;
  names: string;
  uid: string;
  email: string;
  lastLogInAt?: string | null;
  createdAt: string;
  updatedAt: string;
  companiesCount?: number;
  companies?: IAppUserCompany[];

  constructor(data: IAppUser) {
    this.profile = data.profile;
    this.lastName = data.lastName;
    this.names = data.names;
    this.uid = data.uid;
    this.email = data.email;
    this.lastLogInAt = data.lastLogInAt;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.companiesCount = data.companiesCount;
    this.companies = data.companies?.map(
      (company) => ({ idCompany: company.idCompany, name: company.name })
    );
  }

  getFullName(): string {
    return this.fullName;
  }

  /* lastName puede venir vacío: sin trim quedaría un espacio al final. */
  get fullName(): string {
    return `${this.names} ${this.lastName}`.trim();
  }

  /* Iniciales para el avatar: primera letra del nombre y del apellido. */
  get initials(): string {
    const first: string = this.names.trim().charAt(0);
    const last: string = this.lastName.trim().charAt(0);

    return (first + last).toLocaleUpperCase('es-MX');
  }

  get profileLabel(): string {
    return Profile.labelOf(this.profile);
  }

  get profileIcon(): string {
    return Profile.iconOf(this.profile);
  }

  get profileTone(): string {
    return Profile.toneOf(this.profile);
  }

  /* Minúsculas y sin acentos: "garcia" encuentra "García". */
  static normalizeSearch(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  /* El término partido en palabras normalizadas. Un usuario coincide si
     contiene TODAS las palabras, en cualquier orden. */
  static searchWords(term: string): string[] {
    return AppUser.normalizeSearch(term)
      .split(/\s+/)
      .filter(word => word !== '');
  }

  /* Lo que compara el buscador: nombre, apellido y correo. */
  get searchText(): string {
    return AppUser.normalizeSearch(
      `${this.names} ${this.lastName} ${this.email}`
    );
  }

  // Aquí puedes agregar otros métodos útiles relacionados con la clase AppUser
}
